import React, { useState, useCallback, useMemo } from "react";
import ModalErrorInfo from "../../../Project/ModalPublish/ModalErrorInfo";
import { useSnapshot } from "valtio";
import classnames from "classnames";
import {
    Dropdown,
    DropdownMenu,
    DropdownItem,
    DropdownToggle,
} from "reactstrap";
import {
    VerticalDotsIcon,
    SettingsIcon,
    DuplicateIcon,
    TrashIcon,
    EyeIcon,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { store as userStore } from "../../../../components/VerticalLayout/store";
import store, {
    setIsModalAssociateStore,
    setIsModalDissociateOpened,
} from "./store";
import ErrorToast from "@components/Common/ErrorTost";
import { setIsModesModalPublishOpened } from "../store";
import ConsultSvgIcon from "@components/Common/SvgIcons/ConsultSvgIcon";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store";
import { openPosConfigModal } from "./PosConfigurationModal/posConfigSlice";
import { PosProjectRow } from "./PosConfigurationModal/constants";
import { validatePublishEligibility } from "./validators";

type DropDownWrapperProps = {
    element: { selectedRow: { original: PosProjectRow } };
    setSelectedRows: (rows: PosProjectRow | PosProjectRow[]) => void;
    selectedRows: PosProjectRow | PosProjectRow[];
    isProjectArchived: boolean;
    setIsConfirmationModalArchived: (value: boolean) => void;
    setIsConfirmationModalActived: (value: boolean) => void;
    setIsConfirmationModalCopied: (value: boolean) => void;
    associates: Record<string, number>;
    setIsDataUpdated: (value: boolean | number) => void;
};

export function DropDownWrapper({
    element,
    setSelectedRows,
    isProjectArchived,
    setIsConfirmationModalArchived,
    setIsConfirmationModalActived,
    setIsConfirmationModalCopied,
    associates,
}: DropDownWrapperProps): JSX.Element {
    const { t } = useTranslation();
    const reduxDispatch = useDispatch<AppDispatch>();
    const { oneShop, franchiseID, shopID, userID, operatorID } = useSnapshot(
        userStore
    );
    const { isModalAssociateStore, modify } = useSnapshot(store);
    const [isOpen, setIsOpen] = useState(false);
    const [isPublishErrorModalOpened, setIsPublishErrorModalOpened] = useState(
        false
    );
    const [publishErrorMsg, setPublishErrorMsg] = useState("");
    const row = element.selectedRow.original;
    const { projectId, id_boutique, Statut: Status, subRows = [] } = row;
    const hasMultipleAssociates = associates[projectId] > 1 && oneShop;
    const isPublished: boolean = Status === "Publié";
    const isSecondLevel =
        id_boutique !== undefined && !oneShop && projectId !== undefined;

    const shouldRender = useMemo(() => {
        if (!modify || !projectId) return false;
        const isFranchise =
            id_boutique === undefined &&
            !oneShop &&
            row.key_project !== undefined;
        const isShop = id_boutique !== undefined && oneShop;
        return isFranchise || isShop || isSecondLevel;
    }, [
        modify,
        projectId,
        id_boutique,
        oneShop,
        isSecondLevel,
        row.key_project,
    ]);

    const selectRow = useCallback(() => setSelectedRows(row), [
        setSelectedRows,
        row,
    ]);
    const selectRowAsArray = useCallback(() => setSelectedRows([row]), [
        setSelectedRows,
        row,
    ]);

    const handlePublish = useCallback(() => {
        if (subRows.length === 0) {
            ErrorToast(
                t(
                    "Please note that you can't publish a template not associated with one or more POS apps."
                )!
            );
            setSelectedRows([]);
            return;
        }

        const { isValid, errors } = validatePublishEligibility(row, subRows, t);
        if (!isValid) {
            setPublishErrorMsg(
                `${t(
                    "The following levels are missing required configuration"
                )}\n${errors.join("\n")}`
            );
            setIsPublishErrorModalOpened(true);
            return;
        }

        selectRow();
        setIsModesModalPublishOpened(true);
    }, [subRows, t, setSelectedRows, selectRow, row]);

    const handleDuplicate = useCallback(() => {
        selectRowAsArray();
        setIsConfirmationModalCopied(true);
    }, [selectRowAsArray, setIsConfirmationModalCopied]);

    const handleAssociateStore = useCallback(() => {
        selectRow();
        setIsModalAssociateStore(!isModalAssociateStore);
    }, [selectRow, isModalAssociateStore]);

    const handleArchiveOrDelete = useCallback(() => {
        selectRowAsArray();
        if (oneShop) {
            setIsModalDissociateOpened(true);
            return;
        }
        if (!isProjectArchived) {
            if (subRows.length > 0) {
                ErrorToast(
                    t(
                        "Please note that you cannot archive a project associated with the store(s)."
                    )!
                );
            } else {
                setIsConfirmationModalArchived(true);
            }
        } else {
            setIsConfirmationModalActived(true);
        }
    }, [
        oneShop,
        isProjectArchived,
        subRows,
        t,
        selectRowAsArray,
        setIsConfirmationModalArchived,
        setIsConfirmationModalActived,
    ]);

    if (!shouldRender) return <></>;

    const isDisabled = (condition: boolean) =>
        classnames({ "not-allowed-icon__clz": condition });

    const itemStyle: React.CSSProperties = {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const iconSlotStyle: React.CSSProperties = {
        width: 25,
        height: 25,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    // Level 2 (shop rows in franchise): configure
    if (isSecondLevel) {
        return (
            <Dropdown isOpen={isOpen} toggle={() => setIsOpen((prev) => !prev)}>
                <DropdownToggle
                    tag="button"
                    id="page-header-user-dropdown"
                    className={classnames(
                        "pointer__clz btn btn header-item waves-effect",
                        {
                            "not-allowed-icon__clz": isProjectArchived,
                        }
                    )}
                >
                    <VerticalDotsIcon height={25} width={25} />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem
                        style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                        className="pointer__clz cmn_icn_configuration"
                        data-testid="pos-dropdown-configure"
                        onClick={() => {
                            selectRow();
                            reduxDispatch(
                                openPosConfigModal({
                                    row,
                                    level: "shop",
                                    franchiseId: String(franchiseID),
                                    shopId: String(row.id_boutique || shopID),
                                    userId: String(userID),
                                    operatorId: String(operatorID),
                                })
                            );
                        }}
                    >
                        <span style={iconSlotStyle}>
                            <SettingsIcon height={15} width={15} />
                        </span>
                        <span>{t("Configure")}</span>
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        );
    }

    // Level 1 (franchise/project rows): full dropdown
    return (
        <>
            <Dropdown isOpen={isOpen} toggle={() => setIsOpen((prev) => !prev)}>
                <DropdownToggle
                    tag="button"
                    id="page-header-user-dropdown"
                    className="pointer__clz btn btn header-item waves-effect"
                >
                    <VerticalDotsIcon
                        height={25}
                        width={25}
                        className={
                            isProjectArchived
                                ? "cmn_drp_menuProject"
                                : "sco_drp_menuProject"
                        }
                    />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem
                        style={itemStyle}
                        className={classnames(
                            isPublished || oneShop
                                ? "cgt_icn_consult"
                                : "cmn_icn_configuration"
                        )}
                        data-testid={`pos-dropdown-${
                            isPublished || oneShop ? "consult" : "configure"
                        }`}
                        onClick={() => {
                            selectRow();
                            reduxDispatch(
                                openPosConfigModal({
                                    row,
                                    level: oneShop ? "shop" : "franchise",
                                    franchiseId: String(franchiseID),
                                    shopId: String(shopID),
                                    userId: String(userID),
                                    operatorId: String(operatorID),
                                    isConsultation: isPublished || oneShop,
                                })
                            );
                        }}
                    >
                        <span style={iconSlotStyle}>
                            {isPublished || oneShop ? (
                                <ConsultSvgIcon height={25} width={25} />
                            ) : (
                                <SettingsIcon height={15} width={15} />
                            )}
                        </span>
                        <span>
                            {t(
                                isPublished || oneShop
                                    ? "To consult"
                                    : "Configure"
                            )}
                        </span>
                    </DropdownItem>

                    {/* Publish */}
                    <DropdownItem
                        style={itemStyle}
                        className={classnames(
                            "cmn_icn_publish",
                            {
                                "not-allowed-icon__clz":
                                    element.selectedRow.original.isArchived ===
                                    true,
                            },
                            isDisabled(isPublished || oneShop)
                        )}
                        data-testid="pos-dropdown-publish"
                        onClick={handlePublish}
                    >
                        <span style={iconSlotStyle}>
                            <DuplicateIcon height={15} width={15} />
                        </span>
                        <span>{t("Publish")}</span>
                    </DropdownItem>

                    {/* Duplicate */}
                    <DropdownItem
                        style={itemStyle}
                        className={classnames(
                            "cmn_icn_duplicate",
                            isDisabled(
                                isProjectArchived ||
                                    hasMultipleAssociates ||
                                    oneShop
                            )
                        )}
                        data-testid="pos-dropdown-duplicate"
                        onClick={handleDuplicate}
                    >
                        <span style={iconSlotStyle}>
                            <DuplicateIcon height={15} width={15} />
                        </span>
                        <span>{t("Duplicate")}</span>
                    </DropdownItem>
                    {!oneShop && (
                        <DropdownItem
                            style={itemStyle}
                            className={classnames(
                                "cmn_icn_associateStore",
                                isDisabled(isProjectArchived || isPublished)
                            )}
                            data-testid="pos-dropdown-associate"
                            onClick={handleAssociateStore}
                        >
                            <span style={iconSlotStyle}>
                                <DuplicateIcon height={15} width={15} />
                            </span>
                            <span>{t("Associate to store(s)")}</span>
                        </DropdownItem>
                    )}

                    <DropdownItem
                        style={itemStyle}
                        className={classnames(
                            isDisabled(isPublished || oneShop),
                            isProjectArchived
                                ? "cmn_icn_FRunarchive"
                                : "cmn_btn_FRunarchive"
                        )}
                        data-testid={`pos-dropdown-${
                            isProjectArchived
                                ? "enable"
                                : oneShop
                                ? "delete"
                                : "archive"
                        }`}
                        onClick={handleArchiveOrDelete}
                    >
                        <span style={iconSlotStyle}>
                            {isProjectArchived ? (
                                <EyeIcon height={15} width={15} />
                            ) : (
                                <TrashIcon height={15} width={15} />
                            )}
                        </span>
                        <span className={isDisabled(isPublished)}>
                            {isProjectArchived
                                ? t("Enable")
                                : t(oneShop ? "Delete" : "Archive")}
                        </span>
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
            {isPublishErrorModalOpened && (
                <ModalErrorInfo
                    setIsModalOpened={setIsPublishErrorModalOpened}
                    isModalOpened={isPublishErrorModalOpened}
                    msg={publishErrorMsg}
                />
            )}
        </>
    );
}
